import { apiService } from '../../../shared/services/apiServices';
import {
  type AdminPrivileges,
  initialAdminPrivileges,
} from '../types/adminPrivilegeTypes';

interface PrivilegePermission {
  key: string;
  name: string;
  allowed: boolean;
}

interface PrivilegeModule {
  key: string;
  name: string;
  permissions?: PrivilegePermission[];
}

function transformPrivilegesResponse(
  data: Record<string, PrivilegeModule>
): AdminPrivileges {
  const flat: Record<string, boolean> = {};

  Object.values(data).forEach((module) => {
    module.permissions?.forEach((perm) => {
      flat[perm.key] = perm.allowed;
    });
  });

  // Build the result from initialAdminPrivileges keys so the shape is always
  // complete — unknown keys from the API are silently ignored.
  const result = { ...initialAdminPrivileges };
  (Object.keys(result) as Array<keyof AdminPrivileges>).forEach((key) => {
    if (key in flat) {
      result[key] = flat[key] ?? false;
    }
  });

  return result;
}

/**
 * @deprecated Use policy-management/permissions via useUserPrivileges() hook for authorization checks.
 *
 * This API is kept for DISPLAY-ONLY purposes (e.g., AdminPrivilegesView component).
 * It returns a specific user's privileges for display in the UI.
 *
 * For authorization checks (checking if the caller has permission to perform an action),
 * use the universal useUserPrivileges() hook which calls GET /permissions (no userId param).
 */
export const getAdminPrivilegesApiService = async (
  id: string
): Promise<AdminPrivileges | null> => {
  const url = `loopay-user-management/users/${id}/privileges`;

  try {
    const result = await apiService.getAPI(url, {
      skipGlobalErrorRedirect: true,
    });

    if (!result.error) {
      const responseData = result.data as {
        privileges: Record<string, PrivilegeModule>;
      };
      const rawPrivileges = responseData.privileges;
      if (!rawPrivileges) {
        return null;
      }
      return transformPrivilegesResponse(rawPrivileges);
    }

    return null;
  } catch {
    return null;
  }
};
